import type { ReactNode } from 'react';
import { AlertCircle } from 'lucide-react';

import { cn } from '@/lib/cn';

/**
 * Shared label / hint / error scaffolding for every form control.
 *
 * Wiring the ids by hand in each control is how `aria-describedby` ends up
 * pointing at nothing, so it happens once here. An error replaces the hint
 * rather than stacking below it: two lines of small text under a field is how
 * people stop reading either.
 */

export interface FieldWrapperProps {
  id: string;
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  /** Marks the control required and shows the marker next to the label. */
  required?: boolean;
  className?: string;
  children: ReactNode;
}

export function fieldIds(id: string): { hintId: string; errorId: string } {
  return { hintId: `${id}-hint`, errorId: `${id}-error` };
}

/** `aria-describedby` value for a control, or undefined when there is nothing to describe. */
export function describedBy(
  id: string,
  options: { hint?: unknown; error?: unknown },
): string | undefined {
  const { hintId, errorId } = fieldIds(id);
  const parts: string[] = [];
  if (options.error) parts.push(errorId);
  else if (options.hint) parts.push(hintId);
  return parts.length > 0 ? parts.join(' ') : undefined;
}

export function FieldLabel({
  htmlFor,
  children,
  required,
  className,
}: {
  htmlFor: string;
  children: ReactNode;
  required?: boolean;
  className?: string;
}): JSX.Element {
  return (
    <label
      htmlFor={htmlFor}
      className={cn('block text-sm font-medium text-ink', className)}
    >
      {children}
      {required ? (
        <span className="ml-1 text-status-missed" aria-hidden="true">
          *
        </span>
      ) : null}
    </label>
  );
}

export function FieldMessage({
  id,
  tone,
  children,
}: {
  id: string;
  tone: 'hint' | 'error';
  children: ReactNode;
}): JSX.Element {
  const isError = tone === 'error';
  return (
    <p
      id={id}
      // An error appearing after a failed save has to be announced, not just
      // painted red.
      role={isError ? 'alert' : undefined}
      className={cn(
        'flex items-start gap-1.5 text-xs leading-5',
        isError ? 'text-status-missed' : 'text-muted',
      )}
    >
      {isError ? (
        <AlertCircle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
      ) : null}
      <span>{children}</span>
    </p>
  );
}

export function FieldWrapper({
  id,
  label,
  hint,
  error,
  required,
  className,
  children,
}: FieldWrapperProps): JSX.Element {
  const { hintId, errorId } = fieldIds(id);
  return (
    <div className={cn('flex w-full flex-col gap-1.5', className)}>
      {label ? (
        <FieldLabel htmlFor={id} required={required}>
          {label}
        </FieldLabel>
      ) : null}
      {children}
      {error ? (
        <FieldMessage id={errorId} tone="error">
          {error}
        </FieldMessage>
      ) : hint ? (
        <FieldMessage id={hintId} tone="hint">
          {hint}
        </FieldMessage>
      ) : null}
    </div>
  );
}

/** The one input skin, so Input / NumberInput / Textarea / Select cannot drift. */
export const controlClassName = [
  'w-full rounded-lg border bg-surface text-ink',
  'placeholder:text-muted/70',
  'transition-colors duration-150 ease-out',
  'outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
  'disabled:cursor-not-allowed disabled:opacity-60',
  'read-only:bg-raised/50',
].join(' ');

export function controlTone(hasError: boolean): string {
  return hasError
    ? 'border-status-missed focus-visible:outline-status-missed'
    : 'border-line hover:border-line/80';
}
